import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, concat, map, of, switchMap, take } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';
import { PracticeContextService } from '@beyou/app/core/services/practice-context.service';
import { PartnerDashboardService } from '@beyou/app/core/store/features/partner-dashboard.service';
import { ClientsDataService } from '@beyou/app/features/clients/services/clients-data.service';
import { UserPartnerChainLink, UserRelationships } from '@beyou/app/core/models/users/user-relationships.model';
import { primaryTierKey } from '@beyou/app/core/models/partners/partner-reseller.model';

/** The four partner profiles a starter page exists for; whether they earn commission tells the professionals apart. */
export type StarterProfile =
  | 'strategic'
  | 'networkPartner'
  | 'professional'
  | 'professionalWithCommission';

/** Known once the profile and its activity are read; `profile` is null when the regular overview applies. */
export type StarterState = {
  profile: StarterProfile | null;
};

/**
 * Whether the viewer should see the starter overview instead of the metrics: they hold a
 * partner profile and have done nothing with it yet. Judged from data the app already has.
 *
 * The answer is worked out once the viewer's ties are known (the app injects this at start-up
 * for that) and kept; a dashboard visit reads what is there and asks for a silent re-check, so
 * the page never shows a loader once it has an answer. A fresh read of the ties (e.g. after the
 * partner finished setup) drops the answer until it is worked out again.
 */
@Injectable({ providedIn: 'root' })
export class StarterOverviewService {
  private readonly state = new BehaviorSubject<StarterState | null>(null);

  /** `null` while the answer is not known yet, so a page shows its loader rather than a flash. */
  readonly starter$: Observable<StarterState | null> = this.state.asObservable();

  private readonly relationships$: Observable<UserRelationships | null>;

  constructor(
    private practiceContextService: PracticeContextService,
    private partnerDashboardService: PartnerDashboardService,
    private clientsDataService: ClientsDataService
  ) {
    this.relationships$ = this.practiceContextService.relationships$;
    this.relationships$
      .pipe(
        switchMap((relationships) =>
          relationships === null ? of(null) : concat(of(null), this.evaluate(relationships))
        )
      )
      .subscribe((state) => this.state.next(state));
  }

  /** Re-checks the activity behind the current answer without dropping it; a first check is left alone. */
  refresh(): void {
    if (this.state.value === null) return;
    this.relationships$
      .pipe(
        take(1),
        filter((relationships): relationships is UserRelationships => relationships !== null),
        switchMap((relationships) => this.evaluate(relationships))
      )
      .subscribe((state) => this.state.next(state));
  }

  private evaluate(relationships: UserRelationships): Observable<StarterState> {
    const profile = profileOf(relationships.partnerChain[0] ?? null);
    if (!profile) return of<StarterState>({ profile: null });
    return this.hasDoneNothing(profile).pipe(map((nothingDone) => ({ profile: nothingDone ? profile : null })));
  }

  /** "Nothing done yet", per profile. A failed read answers no, so the regular overview shows. */
  private hasDoneNothing(profile: StarterProfile): Observable<boolean> {
    return this.partnerDashboardService.load().pipe(
      switchMap(({ partner }) => {
        switch (profile) {
          case 'strategic':
            return of(partner.childPartnersCount === 0);
          case 'networkPartner':
            return of((partner.linkedProfessionalsCount ?? 0) === 0);
          default:
            return partner.referredUsersCount === 0 ? this.hasNoClients() : of(false);
        }
      }),
      catchError(() => of(false))
    );
  }

  /**
   * A Professional can also add clients from the client list, so a linked client counts as done.
   * The owner's own link, there so they can make sessions for themselves, is not a client yet.
   */
  private hasNoClients(): Observable<boolean> {
    return this.practiceContextService.resolvedPractice$.pipe(
      take(1),
      switchMap((practice) => {
        if (!practice) return of(true);
        return this.clientsDataService
          .loadClientsPage({ pageSize: 5 })
          .pipe(map((clients) => clients.total_records === clients.data.filter((client) => client.isPracticeOwner).length));
      })
    );
  }
}

/** The most important partner role first: strategic, then network partner, then the professional with or without commission. */
function profileOf(partner: UserPartnerChainLink | null): StarterProfile | null {
  if (!partner) return null;
  switch (primaryTierKey(partner.tiers)) {
    case 'super':
      return 'strategic';
    case 'power':
      return 'networkPartner';
    case 'network':
      if (partner.earnsCommission === null) return null;
      return partner.earnsCommission ? 'professionalWithCommission' : 'professional';
    default:
      return null;
  }
}
